import React, { useState } from "react";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@mui/material";
import { toast } from "sonner";
import config from '../../config/config';
import { Loader2 } from "lucide-react";

const ContactFormSchema = z.object({
  name: z.string().min(1, "Bitte geben Sie Ihren Namen ein."),
  email: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein."),
  message: z.string().min(1, "Bitte geben Sie eine Nachricht ein."),
  phone: z
    .string()
    .optional()
    .refine((val) => !val || /^\+?[0-9\s-]+$/.test(val), "Bitte geben Sie eine gültige Telefonnummer ein."),
  address: z
    .string()
    .optional()
    .refine((val) => !val || val.length > 5, "Bitte geben Sie eine vollständige Adresse ein."),
  agbAccepted: z.boolean().refine((val) => val === true, "Sie müssen die AGB akzeptieren."),
});

export default function ContactForm({ fields = ["name", "email", "message", "phone", "address", "agbAccepted"], redirect_url, button, ratgeberName, merkmal, immobilie, toastText, event }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    phone: "",
    address: "",
    agbAccepted: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Dynamically pick required fields from the schema
    const schema = ContactFormSchema.pick(
      fields.reduce((acc, field) => ({ ...acc, [field]: true }), {})
    );
    const validation = schema.safeParse(formData);
  
    if (!validation.success) {
      const errorMap = validation.error.errors.reduce((acc, err) => {
        acc[err.path[0]] = err.message;
        return acc;
      }, {});
      setErrors(errorMap);
      setLoading(false);
      return;
    }
  
    // Clear errors if validation passes
    setErrors({});
  
    try {
      const response = await fetch(`${config.apiUrl}/api/create-contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The server endpoint expects the form data to be nested under "client"
        body: JSON.stringify({
          first_name: formData.name.split(" ")[0],
          last_name: formData.name.split(" ").slice(1).join(" ") || null,
          email: formData.email,
          home_phone: formData.phone || null,
          description: formData.message,
          address: formData.address || null,
          accept_contact: formData.agbAccepted,
          merkmal: merkmal || null,
          immobilie: immobilie || null,
        }),
      });
  
      if (!response.ok) {
        throw new Error("Server error");
      }
  
      const data = await response.json();
      setFormData({})
      toast.success(`${toastText} erfolgreich angefragt!`);
      if (event) {
        event();
      }
      setSuccess(true);

      // You can use data.client_id if needed
      if (redirect_url) {
        try {
          const blob = await (await fetch(redirect_url)).blob();
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.download = `${ratgeberName}.pdf`;
          anchor.click();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Error downloading file:", error);
        }
      }
    } catch (error) {
      console.error("Error creating contact:", error);
      toast.error("Fehler beim Erstellen des Kontakts.");
    }finally {
      setLoading(false);
    }
  };
  
  if (success) {
    return <p className="text-green-500">Vielen Dank! Ihre Anfrage wurde erfolgreich gesendet.</p>;
  }
  return (
    <form className="space-y-4 font-sans max-w-xl mx-auto text-left w-full" onSubmit={handleSubmit}>
      {fields.includes("name") && (
        <div>
          <label htmlFor="name" className="block mb-1">
            Name<span className="text-red-500">*</span>
          </label>
          <Input
            id="name"
            name="name"
            placeholder="Ihr Name"
            value={formData.name}
            onChange={handleInputChange}
            className={errors.name ? "border-red-500" : ""}
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>
      )}

      {fields.includes("email") && (
        <div>
          <label htmlFor="email" className="block mb-1">
            E-Mail-Adresse<span className="text-red-500">*</span>
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Ihre E-Mail-Adresse"
            value={formData.email}
            onChange={handleInputChange}
            className={errors.email ? "border-red-500" : ""}
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>
      )}

      {fields.includes("phone") && (
        <div>
          <label htmlFor="phone" className="block mb-1">
            Telefonnummer
          </label>
          <Input
            id="phone"
            name="phone"
            type="number"
            placeholder="Ihre Telefonnummer"
            value={formData.phone}
            onChange={handleInputChange}
            className={errors.phone ? "border-red-500" : ""}
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>
      )}

      {fields.includes("address") && (
        <div>
          <label htmlFor="address" className="block mb-1">
            Adresse
          </label>
          <Input
            id="address"
            name="address"
            placeholder="Ihre Adresse"
            value={formData.address}
            onChange={handleInputChange}
            className={errors.address ? "border-red-500" : ""}
          />
          {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
        </div>
      )}

      {fields.includes("message") && (
        <div>
          <label htmlFor="message" className="block mb-1">
            Nachricht<span className="text-red-500">*</span>
          </label>
          <Textarea
            id="message"
            name="message"
            placeholder="Ihre Nachricht"
            value={formData.message}
            onChange={handleInputChange}
            className={errors.message ? "border-red-500" : ""}
          />
          {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
        </div>
      )}

      {fields.includes("agbAccepted") && (
        <div>
          <label htmlFor="agbAccepted" className="flex items-center">
            <Checkbox
              id="agbAccepted"
              name="agbAccepted"
              checked={formData.agbAccepted}
              onChange={handleInputChange}
            />
            <span className="ml-2">
              Ich akzeptiere die{" "}
              <a href="/unternehmen/datenschutz" target="_blank" className="text-blue-500 underline">
                Datenschutz
              </a>
            </span>
          </label>
          {errors.agbAccepted && <p className="text-red-500 text-sm mt-1">{errors.agbAccepted}</p>}
        </div>
      )}

      <Button disabled={loading} type="submit" className="w-full">
        {loading? <Loader2 className="animate-spin mr-2" />:null}
        {button?button:"Senden"}
      </Button>
    </form>
  );
}